import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as Popover from '@radix-ui/react-popover';
import { Bell, MailX } from 'lucide-react';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { timeAgo } from '../lib/format';
import { Button, Spinner } from './ui';

export function NotificationBell({ linkBase }: { linkBase: 'admin' | 'portal' }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['notifications'], queryFn: () => api('/notifications'), refetchInterval: 60_000 });
  // Live updates via SSE (falls back to the 60s poll above).
  useEffect(() => {
    let es: EventSource | null = null;
    try {
      es = new EventSource('/api/v1/events');
      es.addEventListener('notifications', () => qc.invalidateQueries({ queryKey: ['notifications'] }));
    } catch {
      /* SSE unsupported */
    }
    return () => es?.close();
  }, [qc]);
  const unread = q.data?.unread ?? 0;
  const fix = (link: string) =>
    linkBase === 'portal'
      ? link.replace(/^\/admin\/review\//, '/portal/review/').replace(/^\/admin\/projects\/([a-f0-9]{24})\?tab=picklist$/, '/portal/picklist/$1')
      : link.replace(/^\/portal\/review\//, '/admin/review/').replace(/^\/portal\/picklist\/([a-f0-9]{24})$/, '/admin/projects/$1?tab=picklist');
  return (
    <Popover.Root onOpenChange={(o) => o && unread && api('/notifications/read', { body: {} }).then(() => qc.invalidateQueries({ queryKey: ['notifications'] }))}>
      <Popover.Trigger asChild>
        <button className="relative flex size-9 items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 data-[state=open]:bg-slate-100" aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}>
          <Bell className="size-[18px]" />
          {unread > 0 && <span className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-semibold text-white ring-2 ring-white">{unread > 9 ? '9+' : unread}</span>}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={6} className="animate-pop-in z-50 w-[min(380px,calc(100vw-16px))] overflow-hidden rounded-2xl border border-line bg-white shadow-pop">
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <span className="flex items-center gap-2 text-sm font-semibold">Notifications {unread > 0 && <span className="rounded-full bg-accent-50 px-1.5 py-0.5 text-[11px] font-medium text-accent-700">{unread} new</span>}</span>
            <Link to={`/${linkBase}/account`} className="text-[12px] text-accent-700 hover:underline">
              Preferences
            </Link>
          </div>
          {q.data && !q.data.email?.configured && (
            <p className="flex items-center gap-1.5 border-b border-line bg-slate-50 px-4 py-2 text-[12px] text-slate-600">
              <MailX className="size-3.5" /> Email delivery isn't configured — in-app only.
            </p>
          )}
          <div className="scrollbar-thin max-h-96 overflow-auto">
            {q.isLoading && (
              <div className="p-4">
                <Spinner />
              </div>
            )}
            {q.data?.notifications?.length === 0 && (
              <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
                <span className="flex size-10 items-center justify-center rounded-full bg-slate-100 text-slate-400"><Bell className="size-5" /></span>
                <p className="text-[13px] text-slate-500">You're all caught up.</p>
              </div>
            )}
            {q.data?.notifications?.map((n: any) => (
              <Popover.Close asChild key={n.id}>
                <Link to={n.link ? fix(n.link) : '#'} className="relative block border-b border-line px-4 py-3 pl-7 transition hover:bg-slate-50">
                  {!n.readAt && <span aria-hidden className="absolute top-[18px] left-3 size-2 rounded-full bg-accent-500" />}
                  <p className={`text-[13px] ${n.readAt ? 'text-slate-700' : 'font-medium text-slate-900'}`}>{n.title}</p>
                  {n.body && <p className="mt-0.5 line-clamp-2 text-[12px] text-slate-500">{n.body}</p>}
                  <p className="mt-1 text-[11px] text-slate-400">{timeAgo(n.createdAt)}</p>
                </Link>
              </Popover.Close>
            ))}
          </div>
          {q.isError && (
            <div className="p-3">
              <Button size="sm" onClick={() => q.refetch()}>
                Retry
              </Button>
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
